import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
    Box, Heading, Text, HStack, VStack, Card, CardHeader, CardBody, 
    Icon, Tag, Select, IconButton, Button, useColorModeValue
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, Calendar, FileDown } from 'lucide-react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useThemeColors } from '@/hooks/useThemeColors';
import { useAccessCheck } from '@/hooks/useAccess';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { apiClient } from '@/lib/api/client';

const DailyOperationsExpensesPage: React.FC = () => {
    const navigate = useNavigate();
    const colors = useThemeColors();
    const tableRef = useRef<DataTableRef>(null);
    const access = useAccessCheck();

    const [filterType, setFilterType] = useState<'weekly' | 'monthly'>('weekly');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isExporting, setIsExporting] = useState(false);
    const [isCsvExporting, setIsCsvExporting] = useState(false);

    const bgColor = useColorModeValue('white', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColor = useColorModeValue('gray.800', 'whiteAlpha.900');

    const [summaryTotals, setSummaryTotals] = useState<any>(null);

    const handleExportPDF = async () => {
        setIsExporting(true);
        const doc = new jsPDF('l', 'mm', 'a4');
        const margin = 14;

        const formatPHP = (val: number) => {
            const absoluteValue = Math.abs(val || 0);
            const formattedNumber = absoluteValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return "P " + formattedNumber; 
        };

        const fetchWithStrictRetry = async (url: string, params: any, attempt = 0): Promise<any> => {
            try {
                return await apiClient.get(url, { params });
            } catch (error: any) {
                if (error.response?.status === 429 && attempt < 3) {
                    await new Promise(r => setTimeout(r, 5000));
                    return fetchWithStrictRetry(url, params, attempt + 1);
                }
                throw error;
            }
        };

        const datesToExport: string[] = [];
        const selectedDate = new Date(currentDate); 
        
        if (filterType === 'weekly') {
            // 🌟 FIXED WEEKLY LOGIC: Anchor to Monday
            const day = selectedDate.getDay(); 
            // Adjust so that Monday is 0, Tuesday is 1... Sunday is 6
            const diff = selectedDate.getDate() - (day === 0 ? 6 : day - 1); 
            
            const monday = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), diff);
            
            for (let i = 0; i < 7; i++) {
                const d = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const dayStr = String(d.getDate()).padStart(2, '0');
                datesToExport.push(`${y}-${m}-${dayStr}`);
            }
        } else {
            const year = selectedDate.getFullYear();
            const month = selectedDate.getMonth();
            const lastDay = new Date(year, month + 1, 0).getDate(); 
            
            for (let i = 1; i <= lastDay; i++) {
                datesToExport.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`);
            }
        }

        try {
            for (let i = 0; i < datesToExport.length; i++) {
                const dateStr = datesToExport[i];

                // Cooldown to prevent rate limiting
                if (i > 0 && i % 5 === 0) await new Promise(r => setTimeout(r, 2000));

                const [issuedRes, truckRes, partsRes, cashRes] = await Promise.all([
                    fetchWithStrictRetry(`/reports/issued-budget`, { transaction_date: dateStr, per_page: 100 }),
                    fetchWithStrictRetry(`/reports/truck-trip-expense`, { transaction_date: dateStr, per_page: 100 }),
                    fetchWithStrictRetry(`/reports/parts-expense`, { transaction_date: dateStr, per_page: 100 }),
                    fetchWithStrictRetry(`/reports/cash-advances`, { transaction_date: dateStr, per_page: 100 })
                ]);
                
                await new Promise(r => setTimeout(r, 600)); 

                if (i > 0) doc.addPage();

                doc.setFontSize(16); doc.setFont("helvetica", "bold");
                doc.text(`Daily Operations: ${new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, margin, 20);

                const sections = [
                    { title: "Issued Budget", data: issuedRes.data.data, head: [['SHIFT', 'AMOUNT', 'SOURCE']], map: (item: any) => [item.shift, formatPHP(item.amount), item.source] },
                    { title: "Truck Trip Expenses", data: truckRes.data.data, head: [['SHIFT', 'HELPER NAME', 'CASH ON HAND', 'ISSUED CASH']], map: (item: any) => [item.shift, item.helper_name, formatPHP(item.cash_on_hand), formatPHP(item.issued_cash_amount)] },
                    { title: "Parts Expenses", data: partsRes.data.data, head: [['SHIFT', 'PLATE NO.', 'RECEIPT NO.', 'ARTICLE', 'QTY', 'AMT/ITEM', 'TOTAL AMT']], map: (item: any) => [item.shift, item.plate_number, item.receipt_no, item.article, item.quantity, formatPHP(item.amount_per_item), formatPHP(item.total_amount)] },
                    { title: "Cash Advances", data: cashRes.data.data, head: [['SHIFT', 'DRIVER NAME', 'HELPER NAME', 'AMOUNT']], map: (item: any) => [item.shift, item.driver_name || '—', item.helper_name || '—', formatPHP(item.amount)] }
                ];

                const hasAnyData = sections.some(s => s.data?.length > 0);
                let currentY = 30;

                if (!hasAnyData) {
                    doc.setFontSize(12); doc.setFont("helvetica", "italic");
                    doc.setTextColor(150, 150, 150);
                    doc.text("No Transactions recorded for this date.", margin, currentY);
                } else {
                    doc.setTextColor(0, 0, 0);
                    sections.forEach(section => {
                        if (section.data?.length > 0) {
                            doc.setFontSize(11); doc.setFont("helvetica", "bold");
                            doc.text(section.title, margin, currentY);
                            autoTable(doc, {
                                startY: currentY + 2,
                                head: section.head,
                                body: section.data.map(section.map),
                                theme: 'striped',
                                headStyles: { fillColor: [40, 40, 40] },
                                margin: { left: margin },
                            });
                            currentY = (doc as any).lastAutoTable.finalY + 10;
                        }
                    });
                }
            }

            doc.save(`Operations_Report_${dateRangeDisplay.replace(/\s+/g, '_')}.pdf`);
        } catch (error: any) {
            alert("Export failed. Please try again in a moment.");
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportCSV = async () => {
        setIsCsvExporting(true);

        const getJsonParams = () => {
            const start = new Date(currentDate);
            const end = new Date(currentDate);

            if (filterType === 'weekly') {
                const day = start.getDay();
                const diff = start.getDate() - day + (day === 0 ? -6 : 1);
                start.setDate(diff);
                end.setDate(start.getDate() + 6);
            } else {
                start.setDate(1);
                end.setMonth(start.getMonth() + 1);
                end.setDate(0);
            }

            return {
                filter_type: filterType,
                start_date: start.toISOString().split('T')[0],
                end_date: end.toISOString().split('T')[0],
            };
        };

        try {
            const params = getJsonParams();
            const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

            const fetchWithRetry = async (url: string, params: any, attempts = 3) => {
                let lastError: any = null;
                for (let i = 0; i < attempts; i++) {
                    try {
                        return await apiClient.get(url, { params, responseType: 'blob' });
                    } catch (err: any) {
                        lastError = err;
                        const status = err?.response?.status;
                        if ((status === 429 || (status >= 500 && status < 600)) && i < attempts - 1) {
                            await delay(1500 * (i + 1));
                            continue;
                        }
                        break;
                    }
                }
                throw lastError;
            };

            const exportsToFetch = [
                { url: '/reports/issued-budget/export', filenamePrefix: 'Issued_Budget' },
                { url: '/reports/truck-trip-expense/export', filenamePrefix: 'Truck_Trip_Expense' },
                { url: '/reports/parts-expense/export', filenamePrefix: 'Parts_Expense' },
                { url: '/reports/cash-advances/export', filenamePrefix: 'Cash_Advances' }
            ];

            for (const item of exportsToFetch) {
                let response: any;
                try {
                    response = await fetchWithRetry(item.url, params, 3);
                } catch (err: any) {
                    try {
                        const blob = err?.response?.data as Blob | undefined;
                        if (blob && typeof blob.text === 'function') {
                            const text = await blob.text();
                            console.error(`${item.url} error response:`, text);
                            alert(`Export failed for ${item.filenamePrefix}: ${text}`);
                        } else {
                            console.error(`Export failed for ${item.url}`, err);
                            alert(`Export failed for ${item.filenamePrefix}. See console for details.`);
                        }
                    } catch (inner) {
                        console.error('Error reading error blob', inner);
                        alert(`Export failed for ${item.filenamePrefix}. See console for details.`);
                    }
                    continue;
                }

                try {
                    const contentType = response.headers?.['content-type'] || '';
                    if (contentType.includes('application/json')) {
                        const text = await (response.data as Blob).text();
                        let parsed;
                        try { parsed = JSON.parse(text); } catch { parsed = null; }
                        const msg = parsed?.message || text || 'Unknown server error';
                        console.error(`${item.url} returned JSON error:`, msg);
                        alert(`Export failed for ${item.filenamePrefix}: ${msg}`);
                        continue;
                    }
                } catch (err) {
                    // ignore and proceed to download if possible
                }

                const filename = `${item.filenamePrefix}_${dateRangeDisplay.replace(/\s+/g, '_')}.csv`;
                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);

                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                await delay(300);
            }
        } catch (error: any) {
            try {
                const blob = error?.response?.data as Blob | undefined;
                if (blob && typeof blob.text === 'function') {
                    const text = await blob.text();
                    console.error('Export CSV failed (server):', text);
                    alert(`Export failed: ${text}`);
                } else {
                    console.error('Export CSV failed', error);
                    alert('Export failed. Please try again in a moment.');
                }
            } catch (inner) {
                console.error('Export CSV failed', inner || error);
                alert('Export failed. Please try again in a moment.');
            }
        } finally {
            setIsCsvExporting(false);
        }
    };

    const adjustDate = (direction: number) => {
        const newDate = new Date(currentDate);
        if (filterType === 'weekly') {
            newDate.setDate(newDate.getDate() + (direction * 7));
        } else {
            newDate.setMonth(newDate.getMonth() + direction);
        }
        setCurrentDate(newDate);
    };

    const dateRangeDisplay = useMemo(() => {
        const start = new Date(currentDate);
        if (filterType === 'weekly') {
            const day = start.getDay();
            const diff = start.getDate() - day + (day === 0 ? -6 : 1); 
            start.setDate(diff);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }, [currentDate, filterType]);

    const tableParams = useMemo(() => {
        const start = new Date(currentDate);
        const end = new Date(currentDate);
        
        if (filterType === 'weekly') {
            const day = start.getDay();
            const diff = start.getDate() - day + (day === 0 ? -6 : 1);
            start.setDate(diff);
            end.setDate(start.getDate() + 6);
        } else {
            start.setDate(1);
            end.setMonth(start.getMonth() + 1);
            end.setDate(0);
        }

        return { 
            filter_type: filterType, 
            start_date: start.toISOString().split('T')[0],
            end_date: end.toISOString().split('T')[0],
        };
    }, [filterType, currentDate]);

    const tableOptions: DataTableOptions = useMemo(
        () => ({
            dataSource: '/reports/summary', 
            primaryKey: 'date', 
            dataKey: 'data',
            showSelection: false,
            showCheckboxes: false,
            softDelete: false,
            displayInModal: false,
            showAction: true,
            showFooter: true,
            noRecordText: 'No operation reports found for this period',
            pagination: true,
            defaultPageSize: 10,
            dataFields: {
                date: { name: 'DATE', withSort: true },
                accounting_day: { name: 'ACCTG DAY', withSort: true },
                accounting_night: { name: 'ACCTG NIGHT', withSort: true },
                truck_expense: { name: 'TRUCKS EXP', withSort: true },
                parts_expense: { name: 'PARTS EXP', withSort: true },
                bale_day: { name: 'BALE DAY', withSort: true },
                bale_night: { name: 'BALE NIGHT', withSort: true },
                total: { name: 'TOTAL'},
                cash_on_hand: { name: 'CASH ON HAND', withSort: true },
            },
            actions: [],
            otherActions: [{ name: 'View Details', link: '/analytics/detailed-expenses' }]
        }),
        [access.can_view]
    );

    const formatCurrency = (val: number | null | undefined) => (
        <Text
            fontWeight="medium"
            color={Number(val) < 0 ? 'red.500' : 'inherit'}
        >
            {val == null
                ? ''
                : new Intl.NumberFormat('en-PH', {
                    style: 'currency',
                    currency: 'PHP',
                }).format(val)}
        </Text>
    );

    const customRenderers = useMemo(() => ({
        date: (val: string) => (
            <Text fontWeight="bold">
                {new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Text>
        ),
        accounting_day: (val: any) => formatCurrency(val),
        accounting_night: (val: any) => formatCurrency(val),
        truck_expense: (val: any) => formatCurrency(val),
        parts_expense: (val: any) => formatCurrency(val),
        bale_day: (val: any) => formatCurrency(val),
        bale_night: (val: any) => formatCurrency(val),
        total: (val: any) => formatCurrency(val),
        cash_on_hand: (val: any) => formatCurrency(val),
    }), []);

    const fetchSummaryTotals = async () => {
        try {
            const start = new Date(currentDate);
            const end = new Date(currentDate);

            if (filterType === 'weekly') {
                const day = start.getDay();
                const diff = start.getDate() - day + (day === 0 ? -6 : 1);
                start.setDate(diff);
                end.setDate(start.getDate() + 6);
            } else {
                start.setDate(1);
                end.setMonth(start.getMonth() + 1);
                end.setDate(0);
            }

            const params = {
                filter_type: filterType,
                start_date: start.toISOString().split('T')[0],
                end_date: end.toISOString().split('T')[0],
            };

            const res = await apiClient.get('/reports/summary-total', { params });
            console.log("SUMMARY RESPONSE FULL:", res);
            console.log("SUMMARY RESPONSE DATA:", res.data?.summary_totals);

            setSummaryTotals(res.data?.summary_totals ?? null);
        } catch (err) {
            console.error('Failed to fetch summary totals:', err);
            setSummaryTotals(null);
        }
    };

    useEffect(() => {
        fetchSummaryTotals();
    }, [filterType, currentDate]);

    return (
        <AdminLayout>
            <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
                <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
                    <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
                        
                        <CardHeader flexShrink={0} py={3}>
                            <HStack justify="space-between" align="center">
                                <Box>
                                    <Heading size="md" mb={1}>Daily Operation Expenses Report</Heading>
                                    <Text fontSize="sm" color={colors.text}>Summary of financial and operational performance per shift</Text>
                                </Box>
                                <HStack spacing={2}>
                                    <Tag colorScheme="blue" variant="subtle" size="sm">{filterType.toUpperCase()} VIEW</Tag>
                                </HStack>
                            </HStack>
                        </CardHeader>

                        <CardHeader py={0} px={0} flexShrink={0}>
                            <Box pt={2} pb={0}>
                                <HStack justify="space-between" align="center" spacing={4} px={5} width="100%">
                                    <HStack spacing={4}>
                                        <Select 
                                            size="sm" w="140px" borderRadius="md" 
                                            value={filterType} 
                                            onChange={(e) => setFilterType(e.target.value as any)}
                                        >
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                        </Select>
                                        
                                        <HStack spacing={1}>
                                            <IconButton icon={<ChevronLeft size={18} />} aria-label="Prev" size="sm" variant="ghost" onClick={() => adjustDate(-1)} />
                                            {/* <HStack 
                                                border="1px solid" borderColor="gray.200" px={4} py={1.5} borderRadius="md" 
                                                bg="white" cursor="pointer" position="relative" _hover={{ borderColor: "blue.400" }}
                                                onClick={() => (document.getElementById('report-date-picker') as HTMLInputElement)?.showPicker()}
                                            >
                                                <Icon as={Calendar} size={14} color="blue.500" />
                                                <Text fontWeight="bold" fontSize="sm" minW="150px" textAlign="center">{dateRangeDisplay}</Text>
                                                <input id="report-date-picker" type="date" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                                                    onChange={(e) => e.target.value && setCurrentDate(new Date(e.target.value))}
                                                />
                                            </HStack> */}
                                            <HStack 
                                                border="1px solid" 
                                                borderColor={borderColor} // Dynamic border
                                                px={4} 
                                                py={1.5} 
                                                borderRadius="md" 
                                                bg={bgColor} // Dynamic background
                                                cursor="pointer" 
                                                position="relative" 
                                                _hover={{ borderColor: "blue.400" }}
                                                onClick={() => (document.getElementById('report-date-picker') as HTMLInputElement)?.showPicker()}
                                            >
                                                <Icon as={Calendar} size={14} color="blue.500" />
                                                <Text 
                                                    fontWeight="bold" 
                                                    fontSize="sm" 
                                                    minW="150px" 
                                                    textAlign="center"
                                                    color={textColor} // Dynamic text color
                                                >
                                                    {dateRangeDisplay}
                                                </Text>
                                                <input 
                                                    id="report-date-picker" 
                                                    type="date" 
                                                    style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                                                    onChange={(e) => e.target.value && setCurrentDate(new Date(e.target.value))}
                                                />
                                            </HStack>
                                            <IconButton icon={<ChevronRight size={18} />} aria-label="Next" size="sm" variant="ghost" onClick={() => adjustDate(1)} />
                                        </HStack>
                                    </HStack>

                                    <HStack>
                                        {/* 🌟 PATTERNED BUTTON: Matches "Create New Truck" style */}
                                        <Button 
                                            leftIcon={<FileDown size={18} />} 
                                            colorScheme="blue" 
                                            size="sm"
                                            px={4}
                                            fontWeight="semibold"
                                            onClick={handleExportCSV}
                                            isLoading={isCsvExporting}
                                            loadingText="Generating..."
                                        >
                                            Export CSV
                                        </Button>
                                        <Button 
                                            leftIcon={<FileDown size={18} />} 
                                            colorScheme="blue" 
                                            size="sm"
                                            px={4}
                                            fontWeight="semibold"
                                            onClick={handleExportPDF}
                                            isLoading={isExporting}
                                            loadingText="Generating..."
                                        >
                                            Export PDF
                                        </Button>
                                    </HStack>
                                </HStack>
                                <Box mx={4} height={4} />
                            </Box>
                        </CardHeader>
               
                        <CardBody p={0} pt={1} pb={4} mx={5} flex="1" display="flex" flexDirection="column" minH={0} overflow="hidden" position="relative">
                            <Box h="100%" display="flex" flexDirection="column" minH={0}>
                                <DataTable
                                    ref={tableRef}
                                    options={tableOptions} 
                                    params={tableParams}
                                    customRenderers={customRenderers}
                                    summaryTotals={summaryTotals}
                                    access={{ can_view: true, can_edit: false, can_create: false, can_delete: false }}
                                    onOpenModal={(rowValue) => {
                                        const targetDate = typeof rowValue === 'object' ? rowValue.date : rowValue;
                                        if (targetDate) navigate(`/analytics/detailed-expenses/${targetDate}`);
                                    }}
                                />
                            </Box>

                        </CardBody>
                                                
                        <CardBody p={0} pt={1} pb={0} mx={5} flex="0" display="flex" flexDirection="column" minH={0} overflow="hidden" position="relative">
                            
                        </CardBody>
                        

                    </Card>
                </VStack> 
            </Box> 
        </AdminLayout>
    );
};

export default DailyOperationsExpensesPage;